import {
  PropertyPortal,
  PropertyPortalPublicationConnectionState,
  PropertyPortalPublicationRecordMode,
  PropertyPortalPublicationStatus,
  type PropertyPortalPublication,
} from 'src/api/generated';
import type { ComponentVariant } from 'src/modules/shared/types/component.type';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';
import {
  getMeliPublicationProviderStatus,
  isMeliPublicationPaymentRequired,
} from './meli-publication.util';

export interface PropertyPublicationStatusDisplay {
  labelKey: TranslationKey;
  tooltipKey: TranslationKey;
  variant: ComponentVariant;
}

export interface PropertyPublicationValueDisplay {
  labelKey: TranslationKey;
  tooltipKey: TranslationKey;
  variant: ComponentVariant;
}

export type PropertyPublicationStatusKind =
  | PropertyPortalPublication['status']
  | 'unpublished'
  | 'queued_publish'
  | 'reference_only'
  | null
  | undefined;

const PROVIDER_STATUS_RESOLVERS: Partial<
  Record<
    PropertyPortal,
    (
      publication: PropertyPortalPublication
    ) => PropertyPublicationStatusDisplay | null
  >
> = {
  [PropertyPortal.MELI]: (publication) => {
    if (isMeliPublicationPaymentRequired(publication)) {
      return {
        labelKey: 'catalog.properties.publications.status.paymentRequired',
        tooltipKey:
          'catalog.properties.publications.statusTooltip.paymentRequired',
        variant: 'warning',
      };
    }

    const providerStatus = getMeliPublicationProviderStatus(publication);
    if (providerStatus === 'under_review') {
      return {
        labelKey: 'catalog.properties.publications.status.underReview',
        tooltipKey: 'catalog.properties.publications.statusTooltip.underReview',
        variant: 'warning',
      };
    }

    if (providerStatus === 'closed' || providerStatus === 'inactive') {
      return {
        labelKey: 'catalog.properties.publications.status.closed',
        tooltipKey: 'catalog.properties.publications.statusTooltip.closed',
        variant: 'secondary',
      };
    }

    return null;
  },
};

const DEFAULT_PROVIDER_LABEL_KEYS: Partial<
  Record<PropertyPortal, TranslationKey>
> = {
  [PropertyPortal.MELI]: 'catalog.properties.editor.publishing.target.meli',
};

const DEFAULT_PROVIDER_BRAND_IDS: Partial<
  Record<PropertyPortal, 'meli' | 'wasi' | 'toppropiedades'>
> = {
  [PropertyPortal.MELI]: 'meli',
};

export const getPropertyPublicationStatusDisplay = (params: {
  publication?: PropertyPortalPublication | null;
  status?: PropertyPublicationStatusKind;
}): PropertyPublicationStatusDisplay => {
  if (params.status === 'reference_only') {
    return {
      labelKey: 'catalog.properties.editor.publishing.status.referenceOnly',
      tooltipKey: 'catalog.properties.publications.statusTooltip.referenceOnly',
      variant: 'secondary',
    };
  }

  if (params.status === 'queued_publish') {
    return {
      labelKey: 'catalog.properties.meli.status.queuedPublish',
      tooltipKey: 'catalog.properties.publications.statusTooltip.queuedPublish',
      variant: 'warning',
    };
  }

  if (
    params.publication?.connectionState ===
    PropertyPortalPublicationConnectionState.REFERENCE_ONLY
  ) {
    return {
      labelKey: 'catalog.properties.editor.publishing.status.referenceOnly',
      tooltipKey: 'catalog.properties.publications.statusTooltip.referenceOnly',
      variant: 'secondary',
    };
  }

  if (params.publication != null) {
    const providerResolver =
      PROVIDER_STATUS_RESOLVERS[params.publication.portal];
    const providerStatus = providerResolver?.(params.publication);
    if (providerStatus) {
      return providerStatus;
    }
  }

  switch (params.status ?? params.publication?.status ?? null) {
    case PropertyPortalPublicationStatus.DRAFT:
      return {
        labelKey: 'catalog.properties.publications.status.draft',
        tooltipKey: 'catalog.properties.publications.statusTooltip.draft',
        variant: 'secondary',
      };
    case PropertyPortalPublicationStatus.PUBLISHED:
      return {
        labelKey: 'catalog.properties.publications.status.published',
        tooltipKey: 'catalog.properties.publications.statusTooltip.published',
        variant: 'success',
      };
    case PropertyPortalPublicationStatus.PAUSED:
      return {
        labelKey: 'catalog.properties.publications.status.paused',
        tooltipKey: 'catalog.properties.publications.statusTooltip.paused',
        variant: 'warning',
      };
    case PropertyPortalPublicationStatus.UNPUBLISHED:
    case 'unpublished':
      return {
        labelKey: 'catalog.properties.publications.status.unpublished',
        tooltipKey: 'catalog.properties.publications.statusTooltip.unpublished',
        variant: 'secondary',
      };
    case PropertyPortalPublicationStatus.ERROR:
      return {
        labelKey: 'catalog.properties.publications.status.error',
        tooltipKey: 'catalog.properties.publications.statusTooltip.error',
        variant: 'danger',
      };
    case PropertyPortalPublicationStatus.UNKNOWN:
    case null:
    default:
      return {
        labelKey: 'catalog.properties.publications.status.error',
        tooltipKey: 'catalog.properties.publications.statusTooltip.unknown',
        variant: 'secondary',
      };
  }
};

export const getPropertyPublicationModeDisplay = (
  publication: PropertyPortalPublication
): PropertyPublicationValueDisplay => {
  switch (publication.recordMode ?? null) {
    case PropertyPortalPublicationRecordMode.MANAGED_PUBLISH:
      return {
        labelKey: 'catalog.properties.publications.mode.managedPublish',
        tooltipKey:
          'catalog.properties.publications.mode.managedPublishTooltip',
        variant: 'primary',
      };
    case PropertyPortalPublicationRecordMode.MANAGED_SYNC:
      return {
        labelKey: 'catalog.properties.publications.mode.managedSync',
        tooltipKey: 'catalog.properties.publications.mode.managedSyncTooltip',
        variant: 'secondary',
      };
    case PropertyPortalPublicationRecordMode.OBSERVED_EXTERNAL:
      return {
        labelKey: 'catalog.properties.publications.mode.observedExternal',
        tooltipKey:
          'catalog.properties.publications.mode.observedExternalTooltip',
        variant: 'secondary',
      };
    case null:
    default:
      return {
        labelKey: 'catalog.properties.publications.mode.unknown',
        tooltipKey: 'catalog.properties.publications.mode.unknownTooltip',
        variant: 'secondary',
      };
  }
};

export const getPropertyPublicationTypeDisplay = (
  publication: PropertyPortalPublication
): PropertyPublicationValueDisplay => {
  switch (publication.connectionState ?? null) {
    case PropertyPortalPublicationConnectionState.CONNECTED:
      return {
        labelKey: 'catalog.properties.publications.type.connected',
        tooltipKey: 'catalog.properties.publications.type.connectedTooltip',
        variant: 'success',
      };
    case PropertyPortalPublicationConnectionState.REFERENCE_ONLY:
      return {
        labelKey: 'catalog.properties.publications.type.referenceOnly',
        tooltipKey: 'catalog.properties.publications.type.referenceOnlyTooltip',
        variant: 'secondary',
      };
    case PropertyPortalPublicationConnectionState.DISCONNECTED:
      return {
        labelKey: 'catalog.properties.publications.type.disconnected',
        tooltipKey: 'catalog.properties.publications.type.disconnectedTooltip',
        variant: 'warning',
      };
    case null:
    default:
      return {
        labelKey: 'catalog.properties.publications.type.unknown',
        tooltipKey: 'catalog.properties.publications.type.unknownTooltip',
        variant: 'secondary',
      };
  }
};

export const getPropertyPublicationProviderLabelKey = (
  portal: PropertyPortal
): TranslationKey | null => DEFAULT_PROVIDER_LABEL_KEYS[portal] ?? null;

export const getPropertyPublicationProviderBrandId = (
  portal: PropertyPortal
): 'meli' | 'wasi' | 'toppropiedades' | null =>
  DEFAULT_PROVIDER_BRAND_IDS[portal] ?? null;

export const getPropertyPublicationOriginDisplay = (
  publication: PropertyPortalPublication
): PropertyPublicationValueDisplay => {
  if (publication.originProvider?.startsWith('meli')) {
    return {
      labelKey: 'catalog.properties.list.provider.meli',
      tooltipKey: 'catalog.properties.publications.origin.meliTooltip',
      variant: 'secondary',
    };
  }

  if (publication.originProvider?.startsWith('wasi')) {
    return {
      labelKey: 'catalog.properties.list.provider.wasi',
      tooltipKey: 'catalog.properties.publications.origin.wasiTooltip',
      variant: 'secondary',
    };
  }

  if (publication.originProvider?.startsWith('toppropiedades')) {
    return {
      labelKey: 'catalog.properties.list.provider.toppropiedades',
      tooltipKey:
        'catalog.properties.publications.origin.toppropiedadesTooltip',
      variant: 'secondary',
    };
  }

  return {
    labelKey: 'catalog.properties.publications.origin.unknown',
    tooltipKey: 'catalog.properties.publications.origin.unknownTooltip',
    variant: 'secondary',
  };
};

export const getPropertyPublicationActivityTimestamp = (
  publication: PropertyPortalPublication
): string =>
  publication.lastSyncSuccessAt ??
  publication.lastSyncAt ??
  publication.updatedAt;
